import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Users, BadgeCheck, ArrowRight } from "lucide-react";
import { Panel, Skeleton, GainBadge } from "../components/ui";
import TierBadge from "../components/TierBadge";
import Avatar from "../components/Avatar";
import Disclaimer from "../components/Disclaimer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useSeo from "../lib/useSeo";
import VerifiedBadge from "../components/VerifiedBadge";
import { useTranslation } from "react-i18next";

const medal = (rank) =>
  rank === 1 ? "bg-amber-100 text-amber-700"
  : rank === 2 ? "bg-slate-200 text-slate-600"
  : rank === 3 ? "bg-orange-100 text-orange-700"
  : "bg-slate-100 text-slate-400";

/**
 * The public, crawlable leaderboard — the product's main SEO and top-of-funnel
 * surface. Renders for logged-out visitors, so it uses the unauthenticated
 * /api/public/leaderboard endpoint, which returns teaser-level data only.
 */
export default function Leaderboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.publicLeaderboard(), api.publicPosts()])
      .then(([l, p]) => { setItems(l.data.investors || []); setPosts(p.data.posts || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useSeo({
    title: t("leaderboard.seoTitle"),
    description: t("leaderboard.seoDesc"),
    jsonLd: items.length ? {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: t("leaderboard.seoTitle"),
      itemListElement: items.slice(0, 25).map((inv, i) => ({
        "@type": "ListItem",
        position: inv.rank || i + 1,
        name: inv.display_name,
        url: `${window.location.origin}/investors/${inv.investor_id}`,
      })),
    } : null,
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <header className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 pill bg-brand-50 text-brand-600 mb-3">
          <Trophy size={13} /> {t("leaderboard.badge")}
        </div>
        <h1 className="text-3xl sm:text-5xl font-heading font-semibold tracking-tighter">
          {t("leaderboard.title")}
        </h1>
        <p className="text-slate-500 mt-3 leading-relaxed">{t("leaderboard.intro")}</p>
        {!user && (
          <Link to="/login" className="btn-primary mt-5 inline-flex" data-testid="lb-signup">
            {t("leaderboard.cta")} <ArrowRight size={16} />
          </Link>
        )}
      </header>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : items.length === 0 ? (
        <p className="text-center text-slate-400 py-10">{t("investors.emptyDesc")}</p>
      ) : (
        <div className="space-y-3" data-testid="leaderboard-list">
          {items.map((inv) => (
            <Link key={inv.investor_id} to={`/investors/${inv.investor_id}`}
                  data-testid={`lb-${inv.investor_id}`}
                  className="card p-4 sm:p-5 flex items-center gap-4 hover:shadow-soft transition-shadow">
              <div className={`w-9 h-9 rounded-xl grid place-items-center font-heading font-semibold text-sm shrink-0 ${medal(inv.rank)}`}>
                {inv.rank}
              </div>
              <Avatar src={inv.avatar} name={inv.display_name} size={38} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-slate-900 truncate">{inv.display_name}</span>
                  {inv.verified && <VerifiedBadge size={15} />}
                  <TierBadge tier={inv.tier} size="xs" />
                </div>
                {inv.username && <div className="text-xs text-slate-400 truncate">@{inv.username}</div>}
                {inv.headline && <div className="text-xs text-slate-400 truncate mt-0.5">{inv.headline}</div>}
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Users size={12} />{inv.follower_count}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                {inv.return_pct === null || inv.return_pct === undefined ? (
                  <span className="text-xs text-slate-400">{t("investors.notRanked")}</span>
                ) : (
                  <>
                    <GainBadge value={inv.return_pct} />
                    <div className="text-[11px] text-slate-400 mt-1">{t("investors.perYear")}</div>
                  </>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {posts.length > 0 && (
        <Panel className="card p-5" testid="lb-posts">
          <h2 className="font-heading font-semibold tracking-tight text-lg mb-3">{t("leaderboard.latestWriting")}</h2>
          <div className="divide-y divide-slate-50">
            {posts.slice(0, 8).map((p) => (
              <Link key={p.slug} to={`/posts/${p.slug}`} className="block py-3 group" data-testid={`lb-post-${p.slug}`}>
                <div className="font-medium text-slate-800 group-hover:text-brand-600">{p.title}</div>
                <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{p.excerpt}</p>
                <div className="text-xs text-slate-400 mt-1">{p.author}</div>
              </Link>
            ))}
          </div>
        </Panel>
      )}

      <Disclaimer />
    </div>
  );
}
